import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, formatZAR } from "@/lib/api";
import { Link } from "react-router-dom";
import { Inbox, FolderKanban, Users, Receipt, ArrowRight, Sparkles, BellRing, Flame, AlertTriangle, Clock, Activity, TrendingUp, Ban, DollarSign, User2, Filter, FileText, UserPlus, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  new_lead: "New leads",
  awaiting_documents: "Awaiting documents",
  in_progress: "In progress",
  lodged: "Lodged",
  completed: "Completed",
};

const PERSONAL_DESK_ROLES = ["lawyer", "back_office"];

const TYPE_FILTERS = [
  { value: "all",     label: "All work" },
  { value: "matter",  label: "Matters" },
  { value: "lead",    label: "Leads" },
  { value: "client",  label: "Clients" },
  { value: "billing", label: "Billing" },
];

const DISCIPLINE_FILTERS = [
  { value: "",                 label: "All disciplines" },
  { value: "conveyancing",     label: "Conveyancing" },
  { value: "antenuptial",      label: "Antenuptial" },
  { value: "trust",            label: "Trust" },
  { value: "will",             label: "Will" },
  { value: "deceased_estate",  label: "Estate" },
];

const TYPE_ICON = {
  matter: FolderKanban,
  lead: UserPlus,
  client: User2,
  billing: CreditCard,
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [matters, setMatters] = useState([]);
  const [leads, setLeads] = useState([]);
  const [ops, setOps] = useState(null);
  const [nudging, setNudging] = useState(false);

  const [me, setMe] = useState(null);
  const [scope, setScope] = useState("firm");
  const [typeFilter, setTypeFilter] = useState("all");
  const [disciplineFilter, setDisciplineFilter] = useState("");

  const canSwitchToMyDesk = useMemo(
    () => Boolean(me && PERSONAL_DESK_ROLES.includes(me.role)),
    [me],
  );

  // Single source of truth for fetching the operations dashboard. Called from
  // (1) the mount effect after /auth/me resolves and (2) the toggle / filter
  // handlers. No useEffect on filters, so each user action issues exactly one
  // request and there's no risk of a stale response overwriting a fresh one.
  const loadOps = (overrides = {}) => {
    const params = {
      scope: overrides.scope ?? scope,
      type: overrides.type ?? typeFilter,
      discipline: overrides.discipline ?? disciplineFilter,
    };
    if (params.type === "all") delete params.type;
    if (!params.discipline) delete params.discipline;
    return api.get("/admin/dashboard/operations", { params })
      .then((r) => setOps(r.data))
      .catch(() => {});
  };

  const switchScope = (nextScope) => {
    if (nextScope === scope) return;
    setScope(nextScope);
    loadOps({ scope: nextScope });
  };

  const switchType = (nextType) => {
    if (nextType === typeFilter) return;
    setTypeFilter(nextType);
    loadOps({ type: nextType });
  };

  const switchDiscipline = (nextDiscipline) => {
    if (nextDiscipline === disciplineFilter) return;
    setDisciplineFilter(nextDiscipline);
    loadOps({ discipline: nextDiscipline });
  };

  // Fetch self FIRST to determine the default scope, then load operations
  // with that resolved scope. Single round-trip pair, no race.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meRes = await api.get("/auth/me");
        if (cancelled) return;
        setMe(meRes.data);
        const defaultScope = PERSONAL_DESK_ROLES.includes(meRes.data?.role)
          ? "my_desk"
          : "firm";
        setScope(defaultScope);
        await loadOps({ scope: defaultScope });
      } catch {
        /* ignore — guarded rendering covers the empty state */
      }
    })();
    api.get("/dashboard/stats").then((r) => !cancelled && setStats(r.data));
    api.get("/matters").then((r) => !cancelled && setMatters(r.data.slice(0, 5)));
    // Phase OTP-3 — "Leads" collapsed into Matters with status='new'.
    api.get("/matters", { params: { status: "new" } }).then((r) => !cancelled && setLeads(r.data.slice(0, 5)));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshOps = () => loadOps();

  const runNudges = async () => {
    setNudging(true);
    try {
      const { data } = await api.post("/admin/run-nudges");
      toast.success(`${data.nudges_sent} nudge${data.nudges_sent === 1 ? "" : "s"} logged.`);
      refreshOps();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
        <div>
          <div className="text-overline text-zinc-500">Practice overview</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Welcome back.</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              if (!window.confirm("Re-provision Estelle + Johnny admin users? Both will be flagged for forced password change on next login.")) return;
              try {
                const { data } = await api.post("/admin/_bootstrap_users", {});
                toast.success(`Bootstrap complete — ${data.actions?.length || 0} actions`);
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50"
            data-testid="bootstrap-admins-btn"
            title="One-time admin user provisioner (idempotent)"
          >
            <UserPlus className="h-4 w-4" /> Provision admins
          </button>
          <button
            onClick={runNudges}
            disabled={nudging}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50 disabled:opacity-50"
            data-testid="run-nudges-btn"
          >
            <BellRing className="h-4 w-4" /> {nudging ? "Sending…" : "Run nudges"}
          </button>
          <Link to="/admin/matters" className="text-sm text-zinc-600 hover:text-zinc-900">
            View all matters →
          </Link>
        </div>
      </div>

      {/* Phase 8.1 — Scope toggle + Phase 8.3 Type & Discipline filters */}
      <div className="flex items-center gap-x-6 gap-y-3 mb-6 flex-wrap" data-testid="dashboard-filters">
        {canSwitchToMyDesk && (
          <div className="flex items-center gap-3" data-testid="scope-toggle">
            <span className="text-overline text-zinc-500">View</span>
            <div className="inline-flex bg-white border border-zinc-200 p-0.5">
              <button
                type="button"
                onClick={() => switchScope("firm")}
                data-testid="scope-firm-btn"
                className={`px-3 py-1.5 text-xs tracking-wide transition-colors ${
                  scope === "firm" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                Whole firm
              </button>
              <button
                type="button"
                onClick={() => switchScope("my_desk")}
                data-testid="scope-my-desk-btn"
                className={`px-3 py-1.5 text-xs tracking-wide transition-colors flex items-center gap-1.5 ${
                  scope === "my_desk" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                <User2 className="h-3 w-3" /> My desk
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap" data-testid="type-filter">
          <span className="text-overline text-zinc-500">Type</span>
          <div className="inline-flex flex-wrap gap-1 bg-white border border-zinc-200 p-0.5">
            {TYPE_FILTERS.map((tf) => (
              <button
                key={tf.value}
                type="button"
                onClick={() => switchType(tf.value)}
                data-testid={`type-filter-${tf.value}`}
                className={`px-2.5 py-1 text-xs tracking-wide transition-colors ${
                  typeFilter === tf.value
                    ? "bg-zinc-900 text-white"
                    : "text-zinc-600 hover:text-zinc-900"
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="discipline-filter">
          <span className="text-overline text-zinc-500">Discipline</span>
          <select
            value={disciplineFilter}
            onChange={(e) => switchDiscipline(e.target.value)}
            data-testid="discipline-filter-select"
            className="bg-white border border-zinc-200 px-2.5 py-1 text-xs text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-400"
          >
            {DISCIPLINE_FILTERS.map((df) => (
              <option key={df.value || "all"} value={df.value}>{df.label}</option>
            ))}
          </select>
        </div>

        {scope === "my_desk" && me && (
          <span className="text-xs text-zinc-500 ml-auto">
            <b>{me.role.replace("_", " ")}</b> · {me.name || me.email}
          </span>
        )}
      </div>

      {/* Phase 8.1 — My Desk summary panel (only when scope=my_desk) */}
      {scope === "my_desk" && ops?.scope === "my_desk" && ops.my_desk_summary && (
        <div className="border border-zinc-200 bg-zinc-50/50 p-6 mb-6 grid grid-cols-2 lg:grid-cols-4 gap-6"
             data-testid="my-desk-summary">
          <DeskMetric label="My active matters" value={ops.my_desk_summary.active_matters}
                      sub={`${ops.my_desk_summary.owned_matters ?? 0} owned · ${ops.my_desk_summary.role_fallback_matters ?? 0} by role`}
                      testid="mydesk-active-matters" />
          <DeskMetric label="Critical for me" value={ops.my_desk_summary.critical_count}
                      sub={`${ops.my_desk_summary.important_count} important · ${ops.my_desk_summary.in_progress_count} in progress`}
                      tone="red"
                      testid="mydesk-critical-count" />
          <DeskMetric label="My revenue at risk" amount={ops.my_desk_summary.revenue_at_risk}
                      sub="unpaid on my critical items"
                      tone="red"
                      testid="mydesk-revenue-at-risk" />
          <DeskMetric label="My outstanding billing" amount={ops.my_desk_summary.outstanding_total}
                      sub="across my active matters"
                      tone="zinc"
                      testid="mydesk-outstanding" />
        </div>
      )}

      {/* Phase 8 — Cash Flow widget */}
      {ops?.cash_flow && (
        <div className="border border-zinc-200 bg-white p-8 mb-6" data-testid="cashflow-widget">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-[#0055FF]" />
              <div className="text-overline text-zinc-500">Firm cash flow</div>
            </div>
            <div className="text-[11px] text-zinc-400">Overdue threshold · {ops.cash_flow.overdue_threshold_days} days</div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <CashMetric label="Outstanding" value={ops.cash_flow.outstanding_total}
              sub={`${ops.cash_flow.counts.draft + ops.cash_flow.counts.issued} invoices`}
              tone="zinc" testid="cf-outstanding" />
            <CashMetric label="Paid this month" value={ops.cash_flow.paid_this_month}
              sub={`${ops.cash_flow.counts.paid} settled`}
              tone="green" testid="cf-paid" />
            <CashMetric label="Overdue" value={ops.cash_flow.overdue_total}
              sub={`${ops.cash_flow.counts.overdue} unpaid >${ops.cash_flow.overdue_threshold_days}d`}
              tone="amber" testid="cf-overdue" />
            <CashMetric label="Revenue at risk" value={ops.cash_flow.blocked_revenue}
              sub={`${ops.cash_flow.counts.blocked} on blocked matters`}
              tone="red" testid="cf-blocked" />
          </div>
        </div>
      )}

      {/* Phase 8 — Priority Board */}
      {ops?.priority_board && (
        <div className="border border-zinc-200 bg-white p-8 mb-10" data-testid="priority-board">
          <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Flame className="h-4 w-4 text-red-600" />
                <div className="text-overline text-zinc-500">Top focus today</div>
              </div>
              <h2 className="font-serif text-3xl mt-1">What needs your attention</h2>
            </div>
            <div className="text-xs text-zinc-500 flex gap-4">
              <span><b>{ops.priority_board.counts.critical}</b> critical</span>
              <span><b>{ops.priority_board.counts.important}</b> important</span>
              <span><b>{ops.priority_board.counts.in_progress}</b> in progress</span>
            </div>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <PriorityColumn
              title="Critical"
              testid="priority-critical"
              icon={Flame}
              tone="red"
              items={ops.priority_board.critical}
              empty="No critical items. Nice."
            />
            <PriorityColumn
              title="Important"
              testid="priority-important"
              icon={AlertTriangle}
              tone="amber"
              items={ops.priority_board.important}
              empty="Nothing urgent waiting."
            />
            <PriorityColumn
              title="In progress"
              testid="priority-in-progress"
              icon={Activity}
              tone="blue"
              items={ops.priority_board.in_progress}
              empty="No active work."
            />
          </div>
        </div>
      )}

      {/* KPI bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPI icon={Inbox} label="New leads" value={stats?.leads_new ?? "—"} sub={`${stats?.leads_total ?? 0} total`} />
        <KPI icon={FolderKanban} label="Active matters" value={stats?.matters_active ?? "—"} sub={`${stats?.matters_total ?? 0} total`} />
        <KPI icon={Users} label="Clients" value={stats?.clients_total ?? "—"} />
        <KPI icon={Receipt} label="Unpaid invoices" value={stats?.invoices_unpaid ?? "—"} />
      </div>

      {/* Pipeline summary */}
      <div className="border border-zinc-200 bg-white p-8 mb-10">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="h-4 w-4 text-[#0055FF]" />
          <div className="text-overline text-zinc-500">Pipeline</div>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {Object.keys(STATUS_LABELS).map((s) => (
            <div key={s} className="border-l border-zinc-200 pl-4 first:border-l-0 first:pl-0">
              <div className="text-overline text-zinc-500">{STATUS_LABELS[s]}</div>
              <div className="font-serif text-4xl font-medium mt-2" data-testid={`pipeline-${s}`}>
                {stats?.matters_by_status?.[s] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Recent matters" link="/admin/matters">
          {matters.length === 0 ? (
            <Empty>No matters yet — they'll appear as intakes complete.</Empty>
          ) : (
            <ul className="divide-y divide-zinc-200 -mx-6">
              {matters.map((m) => (
                <li key={m.id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <Link to={`/admin/matters/${m.id}`} className="font-medium hover:underline">
                      {m.client_name || "—"}
                    </Link>
                    <div className="text-xs text-zinc-500 capitalize">{m.service_type} · {m.status.replace("_", " ")}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-zinc-400" />
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="New matters" link="/admin/matters?status=new">
          {leads.length === 0 ? (
            <Empty>No new matters yet — they arrive via the calculator + OTP wizard.</Empty>
          ) : (
            <ul className="divide-y divide-zinc-200 -mx-6">
              {leads.map((l) => (
                <li key={l.id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{l.client_name || l.name}</div>
                    <div className="text-xs text-zinc-500">
                      {(l.matter_type || "conveyancing").replace(/_/g, " ")} · {l.source || "calculator"}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 bg-zinc-100 text-zinc-600 uppercase tracking-wider">{l.status}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 hover:-translate-y-1 transition-transform duration-200">
      <div className="flex items-center justify-between">
        <div className="text-overline text-zinc-500">{label}</div>
        <Icon className="h-4 w-4 text-zinc-400" />
      </div>
      <div className="font-serif text-5xl font-medium tracking-tight mt-4">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Card({ title, link, children }) {
  return (
    <div className="bg-white border border-zinc-200">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div className="font-medium">{title}</div>
        {link && <Link to={link} className="text-xs text-zinc-500 hover:text-zinc-900">View all →</Link>}
      </div>
      <div className="px-6 py-2">{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="py-6 text-sm text-zinc-500">{children}</div>;
}


// Phase 8.1 — single metric tile for the My Desk summary panel
function DeskMetric({ label, value, amount, sub, tone = "zinc", testid }) {
  const toneMap = {
    zinc:  "text-zinc-900",
    red:   "text-red-700",
    amber: "text-amber-700",
    green: "text-green-700",
  };
  const display = amount !== undefined ? formatZAR(amount || 0) : (value ?? 0);
  return (
    <div data-testid={testid}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className={`font-serif text-3xl font-medium tracking-tight mt-2 ${toneMap[tone]}`}>
        {display}
      </div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}


// Phase 8 — cash-flow metric tile
function CashMetric({ label, value, sub, tone = "zinc", testid }) {
  const toneMap = {
    zinc:  "text-zinc-900",
    green: "text-green-700",
    amber: "text-amber-700",
    red:   "text-red-700",
  };
  return (
    <div data-testid={testid}>
      <div className="text-overline text-zinc-500">{label}</div>
      <div className={`font-serif text-4xl font-medium tracking-tight mt-2 ${toneMap[tone]}`}>
        {formatZAR(value || 0)}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}


// Phase 8 — priority bucket column
function PriorityColumn({ title, items, icon: Icon, tone, empty, testid }) {
  const toneCls = {
    red:   "border-l-red-500   bg-red-50/40",
    amber: "border-l-amber-500 bg-amber-50/30",
    blue:  "border-l-blue-500  bg-blue-50/30",
  }[tone];
  const titleColor = {
    red:   "text-red-700",
    amber: "text-amber-700",
    blue:  "text-blue-700",
  }[tone];
  return (
    <div data-testid={testid} className={`border-l-2 ${toneCls} pl-4`}>
      <div className={`flex items-center gap-2 mb-3 ${titleColor}`}>
        <Icon className="h-4 w-4" />
        <div className="text-overline">{title}</div>
        <span className="text-xs text-zinc-500 ml-auto">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-400 py-6">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <PriorityItem key={`${it.matter_id}-${it.source}-${i}`} item={it} />
          ))}
        </ul>
      )}
    </div>
  );
}


function PriorityItem({ item }) {
  const sevBadge = {
    high:   "bg-red-100 text-red-800",
    medium: "bg-amber-100 text-amber-800",
    low:    "bg-zinc-100 text-zinc-700",
  }[item.severity] || "bg-zinc-100 text-zinc-700";
  const TypeIcon = TYPE_ICON[item.type] || FolderKanban;
  const typeLabel = {
    matter: "Matter", lead: "Lead", client: "Client", billing: "Billing",
  }[item.type] || "Item";
  return (
    <li className="bg-white border border-zinc-200 p-3 hover:border-zinc-400 transition-colors"
        data-testid={`priority-item-${item.reference_id || item.matter_id}`}>
      <Link to={item.link || "#"} className="block">
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 flex items-center gap-1 ${sevBadge}`}>
            <TypeIcon className="h-2.5 w-2.5" /> {typeLabel}
          </span>
          <span className="text-xs text-zinc-500 truncate">{item.client_name}</span>
          {item.owner_label && (
            <span className="text-[10px] text-blue-700 truncate" data-testid="priority-owner-badge">
              · {item.owner_label}
            </span>
          )}
          {item.amount_at_risk > 0 && (
            <span className="ml-auto text-[11px] font-medium text-red-700 whitespace-nowrap"
                  data-testid="priority-amount-at-risk">
              {formatZAR(item.amount_at_risk)} at risk
            </span>
          )}
        </div>
        <div className="text-sm font-medium text-zinc-900 leading-tight">{item.title}</div>
        {item.reasons && item.reasons.length > 0 && (
          <div className="text-[11px] text-zinc-500 mt-1">{item.reasons.join(" · ")}</div>
        )}
      </Link>
    </li>
  );
}
